import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    CreateAccessRequestCrcRequest,
    CreateAccessRequestSSORequest,
    GetInvitationResponse,
    GrantAccessRequestRequest,
    ReInviteRequest,
    RejectAccessRequestRequest,
    RevokeAccessGrantRequest,
    RevokeInvitationRequest,
} from '@app/generated-api';
import { InviteCRCUserRequest } from '@app/generated-api';
import { createLazyLoadedState } from '@app/v2/utility';
import { getSiteBaseUrl } from '@utility/utility';
import { BehaviorSubject, catchError, filter, Observable, tap, throwError } from 'rxjs';
import { GetAllAccessRequestsResponse } from '@app/generated-api';

@Injectable({
    providedIn: 'root',
})
export class AccessRequestService {
    private http = inject(HttpClient);

    private accessRequestsActiveLoading = false;

    private readonly accessRequests = new BehaviorSubject<GetAllAccessRequestsResponse[] | null>(null);

    getAccessRequests() {
        if (this.accessRequests.getValue() == null && this.accessRequestsActiveLoading === false) {
            this.accessRequestsActiveLoading = true;
            this.loadData().subscribe();
        }

        return this.accessRequests.pipe(filter(Boolean));
    }

    loadData = () =>
        this.http.get<GetAllAccessRequestsResponse[]>(`${getSiteBaseUrl()}/api/v2/access`).pipe(
            tap((result) => {
                this.accessRequests.next(result);
                this.accessRequestsActiveLoading = false;
            }),
            catchError((error) => {
                this.accessRequestsActiveLoading = false;
                return throwError(() => error);
            })
        );

    clearAccessRequests() {
        this.accessRequests.next(null);
    }

    reloadAccessRequests() {
        if (this.accessRequestsActiveLoading === false) {
            this.accessRequestsActiveLoading = true;
            this.loadData().subscribe();
        }

        return this.accessRequests.pipe(filter(Boolean));
    }

    readonly countPendingAccessRequests = createLazyLoadedState(() => ({
        fetch$: () => this.http.get<number>(`${getSiteBaseUrl()}/api/v2/access/count`),
    }));

    inviteCRCUser(request: InviteCRCUserRequest) {
        return this.http.post(`${getSiteBaseUrl()}/api/v2/access/invite`, request);
    }

    getInvitation: (guid: string) => Observable<GetInvitationResponse> = (guid: string) => {
        return this.http.get<GetInvitationResponse>(`${getSiteBaseUrl()}/api/v2/access/invitation/${guid}`);
    };

    createAccessRequestCrc: (accessRequest: CreateAccessRequestCrcRequest, key: string) => Observable<number> = (
        accessRequest: CreateAccessRequestCrcRequest,
        key: string
    ) => {
        return this.http.post<number>(`${getSiteBaseUrl()}/api/v2/access/create/crc/${key}`, accessRequest);
    };

    createSSOAccessRequest: (accessRequest: CreateAccessRequestSSORequest) => Observable<number> = (
        accessRequest: CreateAccessRequestSSORequest
    ) => {
        return this.http.post<number>(`${getSiteBaseUrl()}/api/v2/access/create/sso`, accessRequest);
    };

    ifUserExistByGuid: (guid: string) => Observable<boolean> = (guid: string) => {
        return this.http.get<boolean>(`${getSiteBaseUrl()}/api/v2/access/ifUserExist/${guid}`);
    };

    reInvite(request: ReInviteRequest) {
        return this.http.post(`${getSiteBaseUrl()}/api/v2/access/reinvite`, request);
    }

    revokeInvitation(request: RevokeInvitationRequest) {
        return this.http.post(`${getSiteBaseUrl()}/api/v2/access/invitation/revoke`, request);
    }

    rejectAccessRequest(request: RejectAccessRequestRequest) {
        return this.http.post(`${getSiteBaseUrl()}/api/v2/access/reject`, request);
    }

    revokeAccessGrant(request: RevokeAccessGrantRequest) {
        return this.http.post(`${getSiteBaseUrl()}/api/v2/access/revoke-access-grant`, request);
    }

    grantAccessRequest(request: GrantAccessRequestRequest) {
        return this.http.post(`${getSiteBaseUrl()}/api/v2/access/grant`, request);
    }
}
